import { useState, useCallback, useMemo } from 'react';
import { Position } from '@certhon/models';

export interface GeolocationState {
  position: Position | null;
  error: string | null;
  loading: boolean;
  accuracy: number | null;
}

export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

export function useGeolocation(options: GeolocationOptions = {}) {
  const [state, setState] = useState<GeolocationState>({
    position: null,
    error: null,
    loading: false,
    accuracy: null,
  });

  const defaultOptions: PositionOptions = useMemo(
    () => ({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000, // 5 minutes
      ...options,
    }),
    [options],
  );

  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by this browser.',
        loading: false,
      }));
      return Promise.reject(new Error('Geolocation not supported'));
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    return new Promise<Position>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData: Position = {
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              speed: position.coords.speed,
            },
            timestamp: position.timestamp,
          };

          setState({
            position: locationData,
            error: null,
            loading: false,
            accuracy: position.coords.accuracy,
          });

          resolve(locationData);
        },
        (error) => {
          let errorMessage: string;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              errorMessage =
                'An unknown error occurred while retrieving location.';
              break;
          }

          setState((prev) => ({
            ...prev,
            error: errorMessage,
            loading: false,
          }));

          reject(new Error(errorMessage));
        },
        defaultOptions,
      );
    });
  }, [defaultOptions]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({
      position: null,
      error: null,
      loading: false,
      accuracy: null,
    });
  }, []);

  return {
    ...state,
    getCurrentPosition,
    clearError,
    reset,
    isSupported: !!navigator.geolocation,
  };
}
